import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ArrowLeft, Trophy, Quote, Flame, CheckCircle2, Video, Facebook, Instagram, Link2 } from "lucide-react";

const GOLD = "#C9A961";
const RED = "#D90429";

export default function DefiImpact60Results() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/public/defi-impact/results").then((r) => setData(r.data)).catch(() => setData({ ok: false, ambassadeurs: [] })); }, []);

  const cfg = data?.config || {};
  const ambs = data?.ambassadeurs || [];
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "width=640,height=640");
  const copyLink = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Lien copié — colle-le dans ta story ou bio Instagram !"); } catch { toast.error("Copie impossible"); } };

  const ShareBar = () => (
    <div className="flex flex-wrap items-center gap-2" data-testid="results-share">
      <span className="text-xs uppercase tracking-wider text-white/40 mr-1">Partager :</span>
      <button onClick={shareFacebook} className="inline-flex items-center gap-1.5 rounded-full bg-[#1877F2] text-white text-sm font-semibold px-4 py-2 hover:opacity-90 transition-opacity" data-testid="share-facebook"><Facebook size={15} /> Facebook</button>
      <button onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-full text-white text-sm font-semibold px-4 py-2 hover:opacity-90 transition-opacity" style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }} data-testid="share-instagram"><Instagram size={15} /> Instagram</button>
      <button onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 text-white/80 text-sm font-semibold px-4 py-2 hover:border-white/50 transition-colors" data-testid="share-copy"><Link2 size={15} /> Copier le lien</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fafafa]" data-testid="defi-impact-results">
      <header className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 78% 20%, ${GOLD}18 0%, transparent 45%)` }} />
        <div className="relative max-w-5xl mx-auto px-6 py-14">
          <Link to="/defi-impact-60" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-8" data-testid="results-back"><ArrowLeft size={15} /> Retour au défi</Link>
          <div className="chip mb-5 inline-flex items-center gap-2 border border-white/20 px-3 py-1 text-xs uppercase tracking-[0.15em]" style={{ color: GOLD }}><Trophy size={13} /> Résultats & témoignages</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{cfg.nom || "Défi Impact 60"} — <span style={{ color: RED }}>les résultats</span></h1>
          <p className="mt-4 text-lg text-white/70 max-w-2xl">Huit semaines. 16 cours. De vrais débutants, une vraie progression. Voici ce qu'ils ont vécu.</p>
          <div className="mt-6"><ShareBar /></div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14 space-y-10">
        {!data ? (
          <div className="text-white/50">Chargement…</div>
        ) : ambs.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center" data-testid="results-empty">
            <Flame size={30} style={{ color: GOLD }} className="mx-auto mb-3" />
            <div className="font-bold text-lg">Les résultats arrivent bientôt !</div>
            <p className="text-white/60 text-sm mt-2 max-w-md mx-auto">Le défi est en cours. Les bilans et témoignages de nos ambassadeurs seront publiés ici.</p>
            <Link to="/defi-impact-60" className="mt-5 inline-flex items-center gap-2 bg-[var(--g)] text-black px-6 py-3 font-bold uppercase tracking-wide text-sm hover:bg-white transition-colors" style={{ "--g": GOLD }}>Participer au défi</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="results-grid">
            {ambs.map((a, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid={`result-card-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#C9A961]/20 flex items-center justify-center text-[#C9A961] font-bold text-lg">{(a.prenom || "?")[0]}</div>
                  <div>
                    <div className="font-bold text-lg">{a.prenom}</div>
                    <div className="text-xs text-white/50">{a.discipline_desiree || "—"} · {a.niveau || "débutant"}</div>
                  </div>
                </div>
                {typeof a.cours_suivis === "number" && (
                  <div className="inline-flex items-center gap-1.5 text-sm text-emerald-300 mb-3"><CheckCircle2 size={15} /> {a.cours_suivis}/16 cours suivis</div>
                )}
                {a.temoignage_final && (
                  <div className="relative pl-6 text-white/80 text-sm leading-relaxed italic">
                    <Quote size={16} className="absolute left-0 top-0" style={{ color: GOLD }} />
                    {a.temoignage_final}
                  </div>
                )}
                {(a.journal || []).filter((j) => j.type === "video" && j.url).length > 0 && (
                  <div className="mt-4 space-y-1.5">
                    {(a.journal || []).filter((j) => j.type === "video" && j.url).map((j, k) => (
                      <a key={k} href={j.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-sky-400 hover:underline"><Video size={14} /> {j.titre || "Voir la vidéo"}</a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <section className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#111] to-black p-8 text-center" data-testid="results-cta">
          <h3 className="text-2xl font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Ton tour de progresser</h3>
          <p className="text-white/60 mt-2 max-w-xl mx-auto text-sm">Un cours à la fois, à ton rythme. Réserve un cours d'essai (19,99 $) et commence ton propre parcours.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/cours-essai" className="bg-[#D90429] text-white px-6 py-3 font-bold uppercase tracking-wide text-sm hover:bg-white hover:text-[#D90429] transition-colors" data-testid="results-cta-essai">Cours d'essai 19,99 $</Link>
            <Link to="/defi-impact-60" className="border border-white/25 px-6 py-3 font-bold uppercase tracking-wide text-sm hover:border-[var(--g)] hover:text-[var(--g)] transition-colors" style={{ "--g": GOLD }} data-testid="results-cta-defi">Voir le défi</Link>
          </div>
        </section>
      </main>
    </div>
  );
}
